
import React from 'react';
import { ReportData } from '@/types/report';
import { formatDate, formatTime, getProblemsEncountered, getSolutionsApplied, parseDevelopmentReport } from '@/utils/reportFormatters';

type ReportDetailsTableProps = {
  report: ReportData;
}

export const ReportDetailsTable: React.FC<ReportDetailsTableProps> = ({ report }) => {
  // Get development data if available
  const developmentData = parseDevelopmentReport(report);
  
  // Helper function to get value from either parsed development data or direct field
  const getValue = (fieldName: string): string => {
    if (developmentData && developmentData[fieldName] !== undefined) {
      return developmentData[fieldName] || '-';
    }
    
    // Special case for nested fields
    if (fieldName in report) {
      return (report as any)[fieldName] || '-';
    }
    
    return '-';
  };
  
  // Define fields to display with their labels
  const reportFields = [
    { label: 'ชื่อนักเรียน', value: `${report.student_nickname} (${report.student_full_name})` },
    { label: 'ระดับชั้น', value: report.student_grade_level || '-' },
    { label: 'วิชา', value: report.subject || '-' },
    { label: 'ครูผู้สอน', value: report.teacher_display || report.teacher_id || '-' },
    { label: 'วันที่สอน', value: formatDate(report.teaching_date) || '-' },
    { label: 'เวลาสอน', value: report.start_date && report.end_date ? 
      `${formatTime(report.start_date)} - ${formatTime(report.end_date)}` : 
      report.teaching_time_slot || '-' 
    },
    { label: 'ระยะเวลาสอน', value: report.class_duration ? `${report.class_duration} ชั่วโมง` : report.teaching_duration || '-' },
    { label: 'สถานที่สอน', value: report.teaching_location || '-' },
    { label: 'ประเภทการสอน', value: report.teaching_type || '-' },
    
    // Topic learned - from either development data or direct field
    { label: 'หัวข้อที่สอน', value: getValue('topic_learned') },
    { label: 'เนื้อหาที่สอน', value: report.teaching_content || '-' },
    
    // Assessment fields - from development data
    { label: 'ระดับพัฒนาการมิติความรู้', value: getValue('knowledge_rating') },
    { label: 'พฤติกรรมที่สะท้อนถึงความรู้', value: getValue('knowledge_behavior') },
    { label: 'ระดับพัฒนาการมิติทักษะ', value: getValue('skills_rating') },
    { label: 'พฤติกรรมที่สะท้อนถึงทักษะ', value: getValue('skills_behavior') },
    { label: 'ระดับพัฒนาการมิติทัศนคติ', value: getValue('attitude_rating') },
    { label: 'พฤติกรรมที่สะท้อนถึงทัศนคติ', value: getValue('attitude_behavior') },
    
    // Development and problems
    { label: 'พัฒนาการที่เพิ่มขึ้น', value: getValue('student_improvement') },
    { label: 'ปัญหาที่พบ', value: getProblemsEncountered(report) },
    { label: 'วิธีการแก้ไข', value: getSolutionsApplied(report) },
    
    // Homework and next class
    { label: 'การบ้าน/แบบฝึกหัด', value: report.homework_assigned || report.homework_assignment || '-' },
    { label: 'หัวข้อการเรียนครั้งถัดไป', value: report.next_topic || report.next_class_preparation || '-' },
    
    // Submission details
    { label: 'สถานะรายงาน', value: report.status || '-' },
    { label: 'ส่งตรงเวลา', value: report.submitted_on_time === false ? 'ไม่' : 'ใช่' },
    { label: 'บันทึกเพิ่มเติม', value: report.note || '-' },
    { label: 'สาเหตุการปฏิเสธ', value: report.rejection_reason || '-' },
  ];

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold">ข้อมูลรายงานทั้งหมด</h3>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
        <div className="grid grid-cols-1 gap-2">
          {reportFields.map((field, index) => (
            <div key={index} className="grid grid-cols-3 py-2 border-b border-gray-100 last:border-0">
              <div className="col-span-1 font-medium text-gray-700">{field.label}</div>
              <div className="col-span-2 break-words">{field.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
